import { useState, useEffect, useCallback, useRef } from "react";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import { useAuth, useAuthContext } from "../contexts/AuthContext";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { triggerProgressRefresh } from "../../../shared/contexts/ProgressContext";
import { triggerDoctorInfoRefresh } from "../../registration/contexts/DoctorInfoContext";
import OtpInput from "react-otp-input";
import { convertArabicToWesternNumerals } from "../../../shared/utils/numberUtils";
import { useNavigate } from "react-router-dom";
import {
  useLockoutCountdown,
  formatCountdown,
} from "../../../hooks/useLockoutCountdown";
import { WaitKind, retryAfterOf, waitKeys } from "../../../shared/utils/authErrors";
import { takePostLoginPath } from "../../../shared/utils/postLoginPath";

interface OTPScreenProps {
  onNext: (needsScanId?: boolean) => void;
  onBack: () => void;
  onError: (error: string) => void;
}

const OTPScreen = ({ onNext, onBack, onError }: OTPScreenProps) => {
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  // Lockout countdown driven by the backend's `retry_after` (rate limit)
  const { secondsLeft: lockSeconds, isLocked, start: startLockout } =
    useLockoutCountdown();
  // What the countdown is for: too many wrong codes (verify) or too many code requests (send).
  const [waitKind, setWaitKind] = useState<WaitKind>("verify");
  const resendingRef = useRef(false);
  const verifyingRef = useRef(false);

  const authService = useAuth();
  const { checkAuth } = useAuthContext();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const licenseNumber = sessionStorage.getItem("login_license_number") || "";

  // Auto-submit when OTP is complete (6 digits)
  useEffect(() => {
    if (otp.length === 6 && !loading && !isSubmitting && !isLocked) {
      console.log("🚀 Auto-submitting OTP:", otp);
      handleVerifyOTP();
    }
  }, [otp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Start countdown timer
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleVerifyOTP = useCallback(async () => {
    // Blocked by rate limit — don't attempt while the lockout countdown is active
    if (isLocked) return;

    // Prevent duplicate submissions. The ref is the synchronous guard —
    // `isSubmitting` state updates async, so the auto-submit effect (which
    // React Strict Mode runs twice) could otherwise fire two verify calls
    // and burn the limited verify attempts.
    if (verifyingRef.current || isSubmitting || otp.length !== 6) {
      if (otp.length !== 6) {
        setError(t("enter_valid_otp"));
      }
      return;
    }
    verifyingRef.current = true;

    setIsSubmitting(true);
    setLoading(true);
    setError(null);

    try {
      const result = await authService.confirmOtpByLicense(licenseNumber, otp);
      if (result.isSuccess) {
        // Clear license number from storage
        sessionStorage.removeItem("login_license_number");

        // Clear any old payment data from previous session/account
        localStorage.removeItem("paymentId");
        console.log("🧹 Cleared old payment data on successful login");

        // Update authentication state in context
        await checkAuth();

        // Trigger progress and doctor info refresh to get fresh data for the new user
        triggerProgressRefresh();
        triggerDoctorInfoRefresh();
        console.log("🔄 Triggered progress and doctor info refresh after login");

        // Navigate to doctor info registration - useStepRedirect will handle proper routing
        console.log("✅ [OTPScreen] Login successful, navigating to doctor info registration");
        // A page opened while signed out (request link, Qi Card return …) comes first.
        navigate(takePostLoginPath() || "/doctor_info_registration", { replace: true });
      } else {
        // Rate-limited / locked out: start a live countdown from retry_after
        const retryAfter = retryAfterOf(result.errorDetails);
        if (retryAfter > 0) {
          setError(null);
          setOtp("");
          setWaitKind("verify");
          startLockout(retryAfter);
          return;
        }
        setError(result.error || t("invalid_otp"));

        // Check if it's an expired OTP or too many attempts error
        if (
          result.error?.includes("expired") ||
          result.error?.includes("maximum number of attempts")
        ) {
          setOtp("");
          setResendTimer(60);
          setCanResend(false);

          // Restart timer
          const timer = setInterval(() => {
            setResendTimer((prev) => {
              if (prev <= 1) {
                setCanResend(true);
                clearInterval(timer);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("invalid_otp");
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      verifyingRef.current = false;
      setIsSubmitting(false);
      setLoading(false);
    }
  }, [
    otp,
    licenseNumber,
    authService,
    checkAuth,
    navigate,
    t,
    onError,
    isSubmitting,
    isLocked,
    startLockout,
  ]);

  const handleResendOTP = async () => {
    if (!canResend || isLocked) return;
    // Synchronous re-entry guard: state updates are async, so a fast
    // double-tap can fire two resend requests before the button disables.
    if (resendingRef.current) return;
    resendingRef.current = true;

    setLoading(true);
    setError(null);
    setOtp("");

    try {
      const result = await authService.sendOtpByLicense(licenseNumber);
      if (result.isSuccess) {
        setResendTimer(60);
        setCanResend(false);

        // Restart timer
        const timer = setInterval(() => {
          setResendTimer((prev) => {
            if (prev <= 1) {
              setCanResend(true);
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        // 429 otp.rate_limited: too many code requests → countdown until a new code can be requested.
        const retryAfter = retryAfterOf(result.errorDetails);
        if (retryAfter > 0) {
          setWaitKind("send");
          startLockout(retryAfter);
        } else {
          setError(result.error || "Failed to resend OTP");
        }
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to resend OTP";
      setError(errorMessage);
    } finally {
      resendingRef.current = false;
      setLoading(false);
    }
  };

  const formatLicenseNumber = (license: string): string => {
    // Show license number for reference
    return license;
  };

  const handleOtpKeyDown = (event: React.KeyboardEvent) => {
    if (
      event.key === "Enter" &&
      otp.length === 6 &&
      !loading &&
      !isSubmitting
    ) {
      event.preventDefault();
      handleVerifyOTP();
    }
  };

  const verifyButton = (
    <div className="w-full space-y-3">
      {/* Lockout banner: shown when the account is temporarily blocked */}
      {isLocked && (
        <div className="w-full rounded-2xl border-2 border-red-200 bg-red-50 px-4 py-3 text-center">
          <div className="text-sm font-semibold text-red-600">
            {t(waitKeys(waitKind).title)}
          </div>
          <div className="mt-1 text-xs text-red-500">
            {t(waitKeys(waitKind).body)}
          </div>
          <div
            className="mt-1 font-mono text-2xl font-bold text-red-600"
            dir="ltr"
          >
            {formatCountdown(lockSeconds)}
          </div>
        </div>
      )}
      {/* Resend Button */}
      <button
        onClick={handleResendOTP}
        disabled={!canResend || loading || isLocked}
        className={`w-full min-h-[48px] px-8 py-3 rounded-2xl text-base font-semibold transition-all duration-200 ${
          canResend && !loading && !isLocked
            ? "text-[#2e5d7d] bg-gray-100 hover:bg-gray-200 cursor-pointer border-2 border-gray-200"
            : "text-[#999] bg-gray-50 cursor-not-allowed border-2 border-gray-100"
        }`}
      >
        {isLocked
          ? `${t("resend_otp_in")} ${formatCountdown(lockSeconds)}`
          : canResend
          ? t("resend_otp")
          : `${t("resend_otp_in")} ${resendTimer}${t("seconds")}`}
      </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      onBack={onBack}
      showBackButton={false}
      showLanguageToggle={true}
      icon="/ima_logo.png"
      showIcon={true}
      iconSize="w-40 h-32"
      title={t("verify_phone")}
      subtitle={t("enter_code_sent_to")}
      actions={verifyButton}
      maxHeight="95vh"
      centerContent={true}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (otp.length === 6 && !loading && !isSubmitting && !isLocked)
            handleVerifyOTP();
        }}
        className="w-full"
      >
        {/* License Number Display */}
        <div className="w-full text-center mb-6 mt-2">
          <div
            className="flex items-center justify-center gap-2 flex-wrap"
            dir="ltr"
          >
            <div className="text-[#726c6c] text-sm">
              {t("license_number")}:
            </div>
            <div className="text-[#2e5d7d] text-xl font-semibold">
              {formatLicenseNumber(licenseNumber)}
            </div>
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="text-[#2e5d7d] text-sm font-medium hover:text-[#1e4d6d] transition-colors duration-200 cursor-pointer"
            >
              {t("change_license_number")}
            </button>
          </div>
        </div>

        {/* OTP Input */}
        <div className="w-full flex justify-center mb-6">
          <div className="otp-input-container" onKeyDown={handleOtpKeyDown}>
            <OtpInput
              value={otp}
              onChange={(value: string) => {
                // Convert Arabic numerals to Western numerals
                const convertedValue = convertArabicToWesternNumerals(value);
                setOtp(convertedValue);
                setError(null);
              }}
              numInputs={6}
              renderSeparator={<span></span>}
              renderInput={(props) => (
                <input {...props} onKeyDown={handleOtpKeyDown} />
              )}
              inputType="tel"
              shouldAutoFocus
            />
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="w-full text-[#c03744] text-sm font-normal text-center mb-4">
            {error}
          </div>
        )}

        {/* Helper Text */}
        <div className="w-full text-center text-[#726c6c] text-xs font-normal leading-relaxed">
          {t("didnt_receive_code")}
        </div>
      </form>
    </ResponsiveFormLayout>
  );
};

export default OTPScreen;
